import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/firebase';
import { logger } from '@/utils/logger';

export interface EmailConsentData {
  emailConsent: boolean;
  consentDate: string;
  consentVersion: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Service for managing user email consent preferences
 */
export class EmailConsentService {
  private static readonly CONSENT_VERSION = '1.0';

  /**
   * Update user's email consent preference
   */
  static async updateEmailConsent(
    userId: string, 
    consent: boolean,
    metadata?: Partial<EmailConsentData>
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      
      const consentData: EmailConsentData = {
        emailConsent: consent,
        consentDate: new Date().toISOString(),
        consentVersion: this.CONSENT_VERSION,
        ipAddress: metadata?.ipAddress,
        userAgent: metadata?.userAgent || navigator.userAgent,
      };

      await updateDoc(userRef, {
        'profile.emailConsent': consent,
        'profile.emailConsentData': consentData,
        'profile.lastUpdated': new Date().toISOString()
      });

      logger.info('EmailConsentService', 'Email consent updated', {
        userId,
        consent,
        version: this.CONSENT_VERSION
      });

      // Track consent changes for analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_consent_updated', {
          consent_given: consent,
          consent_version: this.CONSENT_VERSION
        });
      }

    } catch (error) {
      logger.error('EmailConsentService', 'Failed to update email consent', {
        userId,
        consent,
        error
      });
      throw error;
    }
  }

  /**
   * Get user's current email consent status
   */
  static async getEmailConsent(userId: string): Promise<boolean> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data();
      return userData.profile?.emailConsent || false;

    } catch (error) {
      logger.error('EmailConsentService', 'Failed to get email consent', {
        userId,
        error
      });
      return false;
    }
  }

  /**
   * Record email consent during user registration
   */
  static async recordInitialConsent(
    userId: string,
    consent: boolean,
    registrationData?: {
      marketingEmailConsent?: boolean;
      termsAcceptedAt?: string;
    }
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      
      const consentData: EmailConsentData = {
        emailConsent: consent,
        consentDate: new Date().toISOString(),
        consentVersion: this.CONSENT_VERSION,
        userAgent: navigator.userAgent,
      };

      // Update both email consent and marketing consent if provided
      const updateData: Record<string, boolean | string | EmailConsentData> = {
        'profile.emailConsent': consent,
        'profile.emailConsentData': consentData,
        'profile.lastUpdated': new Date().toISOString()
      };

      if (registrationData?.marketingEmailConsent !== undefined) {
        updateData['profile.marketingEmailConsent'] = registrationData.marketingEmailConsent;
      }

      if (registrationData?.termsAcceptedAt) {
        updateData['profile.termsAcceptedAt'] = registrationData.termsAcceptedAt;
      }

      await updateDoc(userRef, updateData);

      logger.info('EmailConsentService', 'Initial consent recorded', {
        userId,
        emailConsent: consent,
        marketingConsent: registrationData?.marketingEmailConsent
      });

    } catch (error) {
      logger.error('EmailConsentService', 'Failed to record initial consent', {
        userId,
        error
      });
      throw error;
    }
  }

  /**
   * Handle unsubscribe requests
   */
  static async unsubscribeUser(userId: string, reason?: string): Promise<void> {
    try {
      await this.updateEmailConsent(userId, false, {
        userAgent: `Unsubscribe: ${reason || 'User request'}`
      });

      logger.info('EmailConsentService', 'User unsubscribed', {
        userId,
        reason
      });

      // Track unsubscribe for analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_unsubscribe', {
          reason: reason || 'user_request'
        });
      }

    } catch (error) {
      logger.error('EmailConsentService', 'Failed to unsubscribe user', {
        userId,
        error
      });
      throw error;
    }
  }

  /**
   * Unsubscribe a DIAGNOSTIC LEAD (no account) via the backend callable. Leads have
   * no userId, so the account-based unsubscribeUser can't reach them; the lead is keyed
   * by sha256(email) and flipped server-side (validated + rate-limited).
   */
  static async unsubscribeLead(leadId: string): Promise<void> {
    try {
      await httpsCallable(functions, 'unsubscribeLead')({ lead: leadId });
      logger.info('EmailConsentService', 'Lead unsubscribed', { leadId });
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_unsubscribe', { reason: 'lead_drip' });
      }
    } catch (error) {
      logger.error('EmailConsentService', 'Failed to unsubscribe lead', { leadId, error });
      throw error;
    }
  }
}